import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { IDoctors, IKeywords, MajorCategories } from '@alpha/types';
import {
  AppLogger,
  Carousel,
  ICardGroupItem,
  ICardItem,
  ICategorySearchItem,
  Icon,
  TranformService,
  Util,
} from 'app-core';
import {
  AssetsRegistrationService,
  CacheService,
  FilterService,
  SearchService,
  UIUtilService,
  UserService,
} from 'app-service';

import CardType = Data.Core.CardType;
import IconType = Data.Core.IconType;
import ImageType = Data.Core.ImageType;
import ShimmerType = Data.Core.ShimmerType;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  viewProviders: [SearchService, TranformService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {
  public cards: Array<ICardGroupItem> = [];
  public carouselItem?: Carousel = new Carousel();
  public categoryItems?: ICategorySearchItem;
  public professionCategoryItems?: ICategorySearchItem;
  public ngUnsubscribe = new Subject<void>();
  public isLoggedIn: boolean = false;
  public drawerOpen: boolean = false;
  public loading: boolean = false;
  public icon: Icon = new Icon({
    icon: IconType.expandMoreIcon,
  });
  public externalLinkIcon: Icon = new Icon({
    icon: IconType.externalLinkIcon,
    focus: true,
    badge: {
      size: 'small',
      color: 'accent',
      content: '',
    },
    click: () => {},
  });
  public shimmerTypeSMCard: ShimmerType = ShimmerType.SMCard;
  private newlyJoinedTxt: string = '';
  private _query: Partial<IKeywords> = {};

  public constructor(
    private readonly _routers: ActivatedRoute,
    private readonly _cacheService: CacheService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _iconService: AssetsRegistrationService,
    private readonly _uiUtilService: UIUtilService,
    private readonly _router: Router,
    private readonly _filterService: FilterService,
    private readonly _searchService: SearchService,
    private readonly _userService: UserService,
    private readonly _tranformService: TranformService,
    private readonly _translocoSvc: TranslocoService,
  ) {}

  public ngOnInit(): void {
    this._getCarouselItems();
    this._getCategoriesList();
    this._isUserLoggedIn();
    this._uiUtilService.isNavbarOpened
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        this.drawerOpen = result;
        this._cdRef.markForCheck();
      });

    this._routers.queryParamMap
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((paramMap: ParamMap) => {
        this._query = Util.processParameters(paramMap);
      });

    this._uiUtilService.isLocationChanged
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (!!this._cacheService.getItem('l')) {
          this._query.location!.subDistrict = [
            Number(this._cacheService.getItem('l')),
          ];
          this._getDoctorsList(this._query);
        }
      });
  }

  private _getDoctorsList(filter: Partial<IKeywords>): void {
    this.loading = true;
    this._searchService
      .getNewlyJoinedDoctors(filter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (list: Array<IDoctors>) => {
          let listObj: Array<ICardItem> = [];
          listObj = this._tranformService.updateLGCardObject(list);
          listObj.forEach(
            (item) =>
              (item.onArrowClick = (item: any) => this.onArrowClick(item)),
          );
          this.cards = [
            {
              items: listObj,
              type: CardType.sm,
              header: {
                categoryTitle: this.newlyJoinedTxt,
                routerLink: ['/search'],
                queryParams: { c: [MajorCategories.Doctor] },
              },
            },
          ];
        },
        (error) => {
          AppLogger.Error(error);
          this.loading = false;
        },
        () => {
          this.loading = false;
          this._cdRef.markForCheck();
        },
      );
  }

  private _getCarouselItems(): void {
    this.carouselItem!.imageItems = [
      {
        src: this._iconService.getImageURL(ImageType.medOne),
      },
      {
        src: this._iconService.getImageURL(ImageType.medTwo),
      },
      {
        src: this._iconService.getImageURL(ImageType.medThree),
      },
    ];
    this._cdRef.markForCheck();
  }

  private _getCategoriesList(): void {
    this.categoryItems = this._filterService.getProfessionCategoriesList(
      this._onCategoryClick,
      this._router,
    );

    this.professionCategoryItems = this._filterService.getProfileCategoriesList(
      this._onProfessionCategoryClick,
      this._router,
    );
    this.getTranslatedCategoryHeaders();
  }

  public getTranslatedCategoryHeaders(): void {
    this._translocoSvc
      .selectTranslate([
        'home-category-header',
        'home-category-orthopedist',
        'home-category-neurologist',
        'home-category-cardiologist',
        'home-category-gynaecologist',
        'home-category-ophthalmologist',
        'home-category-urologist',
        'newly-joined',
      ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        this.professionCategoryItems!.header.categoryTitle = value[0];
        this.professionCategoryItems!.items.forEach((item, index) => {
          item.header = value[index + 1];
        });
        this.newlyJoinedTxt = value[7];
        this._cdRef!.markForCheck();
      });
  }

  private _onCategoryClick(param: number, _router: Router): void {
    _router!
      .navigate(['search'], {
        queryParams: { c: param },
        queryParamsHandling: 'merge', // or 'preserve'
      })
      .catch((err: string | Error) => AppLogger.Error(err));
  }

  private _onProfessionCategoryClick(
    specialityParam: number,
    _router: Router,
  ): void {
    _router!
      .navigate(['search'], {
        queryParams: { c: MajorCategories.Doctor, s: specialityParam },
        queryParamsHandling: 'merge', // or 'preserve'
      })
      .catch((err: string | Error) => AppLogger.Error(err));
  }

  private _isUserLoggedIn(): void {
    this._userService.isLoggedIn.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (result) => {
        this.isLoggedIn = result;
      },
      error: (err) => {
        AppLogger.Warning(`Logged in state update failed; Error:${err}`);
        this.isLoggedIn = false;
      },
    });
  }

  private onArrowClick(item: any): void {
    const route = `/appointment/${item._id}`;
    const allowedParams = ['l', 'c'];
    this._routers.queryParams.subscribe((params) => {
      const updatedParams = Object.keys(params)
        .filter((key) => allowedParams.includes(key)) // Keep only allowed parameters
        .reduce(
          (obj, key) => {
            obj[key] = params[key];
            return obj;
          },
          {} as Record<string, any>,
        );

      this._router
        .navigate([route], {
          queryParams: updatedParams,
        })
        .catch((err: string | Error) => AppLogger.Error(err));
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
